import type { SupportedLanguage } from "../onboarding/constants";
import type { SpeechEvaluation } from "../runtime/types";

export type SpeechBand = "PASS" | "LOW_SCORE" | "FAIL";
export type SpeechTaskFitStatus = "match" | "mismatch" | "unknown";
export type SpeechScoringReason = "clarity" | "task_mismatch";

export interface SpeechAttemptFeedback {
  recognizedText: string;
  displayRecognizedText?: string;
  score: number;
  band: SpeechBand;
  hint: string;
  evaluationScope?: "full" | "prefix";
  evaluatedPattern?: string;
  clarityScore?: number;
  clarityBand?: SpeechBand;
  taskFitStatus?: SpeechTaskFitStatus;
  scoringReason?: SpeechScoringReason;
}

const STRICT_PASS_SCORE_THRESHOLD = 0.9;
const STRICT_LOW_SCORE_THRESHOLD = 0.72;

const ENGLISH_CONTRACTION_RULES: Array<[RegExp, string]> = [
  [/\bit's\b/g, "it is"],
  [/\bi'm\b/g, "i am"],
  [/\bim\b/g, "i am"],
  [/\bi m\b/g, "i am"],
  [/\byou're\b/g, "you are"],
  [/\byoure\b/g, "you are"],
  [/\byou re\b/g, "you are"],
  [/\bwe're\b/g, "we are"],
  [/\bwe re\b/g, "we are"],
  [/\bthey're\b/g, "they are"],
  [/\btheyre\b/g, "they are"],
  [/\bthey re\b/g, "they are"],
  [/\bi've\b/g, "i have"],
  [/\bive\b/g, "i have"],
  [/\bi ve\b/g, "i have"],
  [/\bdon't\b/g, "do not"],
  [/\bdont\b/g, "do not"],
  [/\bdoesn't\b/g, "does not"],
  [/\bdoesnt\b/g, "does not"],
  [/\bdidn't\b/g, "did not"],
  [/\bdidnt\b/g, "did not"],
  [/\bcan't\b/g, "can not"],
  [/\bcant\b/g, "can not"],
  [/\bwon't\b/g, "will not"],
  [/\bwont\b/g, "will not"],
  [/\bisn't\b/g, "is not"],
  [/\bisnt\b/g, "is not"],
  [/\baren't\b/g, "are not"],
  [/\barent\b/g, "are not"],
  [/\bwhat's\b/g, "what is"],
  [/\bwhats\b/g, "what is"],
  [/\bwhere's\b/g, "where is"],
  [/\bwheres\b/g, "where is"],
  [/\bwho's\b/g, "who is"],
  [/\bwhos\b/g, "who is"],
  [/\bhow's\b/g, "how is"],
  [/\bhows\b/g, "how is"],
  [/\bthat's\b/g, "that is"],
  [/\bthats\b/g, "that is"],
];

const ENGLISH_NUMBER_UNITS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
};

const ENGLISH_NUMBER_TEENS: Record<string, number> = {
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const ENGLISH_NUMBER_TENS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const ENGLISH_CANONICAL_TOKEN_MAP: Record<string, string> = {
  ok: "okay",
  okey: "okay",
};

const LATIN_COMPARABLE_TOKEN_EXPANSIONS: Record<string, string[]> = {
  everyday: ["every", "day"],
  wifi: ["wi", "fi"],
};

const ENGLISH_HOMOPHONE_TOKEN_EQUIVALENTS: Record<string, string[]> = {
  hi: ["high"],
  high: ["hi"],
};

const ENGLISH_EXPECTED_TOKEN_STT_ALIASES: Record<string, string[]> = {
  // Android's English recognizer frequently renders the Turkish name "Zeynep"
  // as a phonetic English phrase. Keep this scoped to expected-token matching.
  zeynep: [
    "sayingup",
    "sayingapp",
    "sayingap",
    "sayingnap",
    "sayinget",
    "saynep",
    "saynap",
    "zaynep",
    "zeynap",
    "zaynab",
    "zainab",
    "zenep",
  ],
};

const SPEECH_DISPLAY_TOKEN_PATTERN = /[A-Za-zÀ-ÖØ-öø-ÿİIŞĞÜÖÇışğüöç\u0600-\u06ff0-9]+(?:['’][A-Za-zÀ-ÖØ-öø-ÿİIŞĞÜÖÇışğüöç\u0600-\u06ff0-9]+)*/g;

const AUTHORED_PROPER_NAME_DISPLAY_TOKENS = new Set([
  "ahmet",
  "alex",
  "ali",
  "anna",
  "chris",
  "david",
  "emma",
  "john",
  "kevin",
  "kris",
  "lisa",
  "mark",
  "mehmet",
  "sam",
  "sara",
  "sarah",
  "simon",
  "tom",
  "zeynep",
  "احمد",
  "علي",
  "ساره",
]);

const PROPER_NAME_DISPLAY_STOPWORDS = new Set([
  "a",
  "adim",
  "am",
  "ana",
  "and",
  "are",
  "as",
  "at",
  "ben",
  "benim",
  "bir",
  "bonjour",
  "bu",
  "but",
  "can",
  "ciao",
  "do",
  "el",
  "en",
  "evening",
  "excuse",
  "good",
  "guten",
  "hello",
  "hey",
  "hi",
  "hola",
  "how",
  "i",
  "ich",
  "im",
  "is",
  "iyi",
  "je",
  "la",
  "le",
  "me",
  "mein",
  "merhaba",
  "mi",
  "morning",
  "my",
  "name",
  "nice",
  "no",
  "not",
  "ok",
  "okay",
  "please",
  "salut",
  "say",
  "sono",
  "sorry",
  "soy",
  "thank",
  "thanks",
  "that",
  "the",
  "this",
  "to",
  "un",
  "una",
  "what",
  "where",
  "who",
  "yes",
  "you",
  "your",
  "انا",
  "اسمي",
]);

const LATIN_SPOKEN_LETTER_TOKEN_MAP: Record<string, string> = {
  a: "a",
  ay: "a",
  b: "b",
  be: "b",
  bee: "b",
  c: "c",
  sea: "c",
  see: "c",
  d: "d",
  dee: "d",
  e: "e",
  ee: "e",
  f: "f",
  eff: "f",
  g: "g",
  gee: "g",
  h: "h",
  aitch: "h",
  haitch: "h",
  i: "i",
  eye: "i",
  j: "j",
  jay: "j",
  k: "k",
  kay: "k",
  l: "l",
  ell: "l",
  m: "m",
  em: "m",
  n: "n",
  en: "n",
  o: "o",
  oh: "o",
  p: "p",
  pee: "p",
  q: "q",
  cue: "q",
  queue: "q",
  r: "r",
  are: "r",
  s: "s",
  ess: "s",
  t: "t",
  tea: "t",
  tee: "t",
  u: "u",
  you: "u",
  v: "v",
  vee: "v",
  w: "w",
  doubleyou: "w",
  x: "x",
  ex: "x",
  y: "y",
  why: "y",
  z: "z",
  zed: "z",
  zee: "z",
  ce: "c",
  che: "c",
  de: "d",
  fe: "f",
  ge: "g",
  ha: "h",
  he: "h",
  je: "j",
  ke: "k",
  le: "l",
  me: "m",
  ne: "n",
  pe: "p",
  re: "r",
  se: "s",
  te: "t",
  ti: "t",
  ve: "v",
  ye: "y",
  vay: "w",
  fay: "f",
  "اي": "i",
  "بي": "b",
  "سي": "c",
  "دي": "d",
  "اف": "f",
  "جي": "g",
  "اتش": "h",
  "جاي": "j",
  "كي": "k",
  "ال": "l",
  "ام": "m",
  "ان": "n",
  "او": "o",
  "بيي": "p",
  "كيو": "q",
  "ار": "r",
  "اس": "s",
  "تي": "t",
  "يو": "u",
  "في": "v",
  "دبليو": "w",
  "اكس": "x",
  "واي": "y",
  "زد": "z",
};

const TURKISH_NUMBER_UNITS: Record<string, number> = {
  bir: 1,
  iki: 2,
  uc: 3,
  dort: 4,
  bes: 5,
  alti: 6,
  yedi: 7,
  sekiz: 8,
  dokuz: 9,
};

const TURKISH_NUMBER_TENS: Record<string, number> = {
  on: 10,
  yirmi: 20,
  otuz: 30,
  kirk: 40,
  elli: 50,
  altmis: 60,
  yetmis: 70,
  seksen: 80,
  doksan: 90,
};

const TURKISH_NUMBER_ROOTS = [
  "altmis",
  "doksan",
  "seksen",
  "yetmis",
  "yirmi",
  "sekiz",
  "dokuz",
  "otuz",
  "kirk",
  "elli",
  "alti",
  "dort",
  "yedi",
  "bes",
  "iki",
  "bir",
  "on",
  "uc",
  "yuz",
  "bin",
] as const;

const ARABIC_SCRIPT_PATTERN = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff]/;
const ARABIC_DIACRITIC_PATTERN = /[\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed]/g;
const ARABIC_INDIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const EXTENDED_ARABIC_INDIC_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_HOUR_MARKERS = new Set(["الساعه", "ساعه"]);
const ARABIC_UNIT_NUMBER_WORDS: Record<string, number> = {
  صفر: 0,
  واحد: 1,
  واحده: 1,
  احد: 1,
  احدي: 1,
  اثنان: 2,
  اثنين: 2,
  اثنتان: 2,
  اثنتين: 2,
  اثنا: 2,
  اثني: 2,
  ثلاث: 3,
  ثلاثه: 3,
  اربع: 4,
  اربعه: 4,
  خمس: 5,
  خمسه: 5,
  ست: 6,
  سته: 6,
  سبع: 7,
  سبعه: 7,
  ثمان: 8,
  ثماني: 8,
  ثمانيه: 8,
  تسع: 9,
  تسعه: 9,
};
const ARABIC_ORDINAL_NUMBER_WORDS: Record<string, number> = {
  اول: 1,
  اولي: 1,
  اولى: 1,
  حادي: 1,
  حاديه: 1,
  ثاني: 2,
  ثانيه: 2,
  ثالث: 3,
  ثالثه: 3,
  رابع: 4,
  رابعه: 4,
  خامس: 5,
  خامسه: 5,
  سادس: 6,
  سادسه: 6,
  سابع: 7,
  سابعه: 7,
  ثامن: 8,
  ثامنه: 8,
  تاسع: 9,
  تاسعه: 9,
  عاشر: 10,
  عاشره: 10,
};
const ARABIC_TEEN_PREFIXES: Record<string, number> = {
  احد: 11,
  احدي: 11,
  حادي: 11,
  حاديه: 11,
  ثاني: 12,
  ثانيه: 12,
  اثنا: 12,
  اثني: 12,
  اثنتا: 12,
  اثنتي: 12,
  ثلاث: 13,
  ثلاثه: 13,
  ثالث: 13,
  ثالثه: 13,
  اربع: 14,
  اربعه: 14,
  رابع: 14,
  رابعه: 14,
  خمس: 15,
  خمسه: 15,
  خامس: 15,
  خامسه: 15,
  ست: 16,
  سته: 16,
  سادس: 16,
  سادسه: 16,
  سبع: 17,
  سبعه: 17,
  سابع: 17,
  سابعه: 17,
  ثمان: 18,
  ثماني: 18,
  ثمانيه: 18,
  ثامن: 18,
  ثامنه: 18,
  تسع: 19,
  تسعه: 19,
  تاسع: 19,
  تاسعه: 19,
};
const ARABIC_TEN_WORDS = new Set(["عشر", "عشره"]);
const ARABIC_DIRECT_NUMBER_WORDS: Record<string, number> = {
  عشر: 10,
  عشره: 10,
  احدعشر: 11,
  احديعشر: 11,
  حاديعشر: 11,
  حاديهعشره: 11,
  اثناعشر: 12,
  اثنيعشر: 12,
  اثنتاعشره: 12,
  اثنتيعشره: 12,
  ثلاثهعشر: 13,
  ثلاثهعشره: 13,
  اربعهعشر: 14,
  اربعهعشره: 14,
  خمسهعشر: 15,
  خمسهعشره: 15,
  ستهعشر: 16,
  ستهعشره: 16,
  سبعهعشر: 17,
  سبعهعشره: 17,
  ثمانيهعشر: 18,
  ثمانيهعشره: 18,
  تسعهعشر: 19,
  تسعهعشره: 19,
};
const ARABIC_TENS_NUMBER_WORDS: Record<string, number> = {
  عشرون: 20,
  عشرين: 20,
  ثلاثون: 30,
  ثلاثين: 30,
  اربعون: 40,
  اربعين: 40,
  خمسون: 50,
  خمسين: 50,
  ستون: 60,
  ستين: 60,
  سبعون: 70,
  سبعين: 70,
  ثمانون: 80,
  ثمانين: 80,
  تسعون: 90,
  تسعين: 90,
};

function containsArabicScript(value: string): boolean {
  return ARABIC_SCRIPT_PATTERN.test(String(value || ""));
}

function canonicalizeArabicDigits(value: string): string {
  return Array.from(String(value || ""))
    .map((character) => {
      const arabicIndicIndex = ARABIC_INDIC_DIGITS.indexOf(character);
      if (arabicIndicIndex !== -1) {
        return String(arabicIndicIndex);
      }
      const extendedArabicIndicIndex = EXTENDED_ARABIC_INDIC_DIGITS.indexOf(character);
      if (extendedArabicIndicIndex !== -1) {
        return String(extendedArabicIndicIndex);
      }
      return character;
    })
    .join("");
}

function canonicalizeArabicText(value: string): string {
  return canonicalizeArabicDigits(String(value || ""))
    .replace(ARABIC_DIACRITIC_PATTERN, "")
    .replace(/\u0640/g, "")
    .replace(/[،؛؟]/g, " ")
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/[’`]/g, "'")
    .replace(/[-/]/g, " ")
    .replace(/[^\u0600-\u06ff0-9'\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function canonicalizeMixedScriptSpeechText(value: string): string {
  return canonicalizeArabicDigits(String(value || ""))
    .replace(/\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/g, (_match, hour: string, minute: string) => (
      Number(minute) === 0 ? hour : `${hour} ${Number(minute)}`
    ))
    .replace(/\b(?:[A-Za-z]\.){2,}/g, (match) => collapseDottedLatinInitialisms(match))
    .replace(ARABIC_DIACRITIC_PATTERN, "")
    .replace(/\u0640/g, "")
    .replace(/[،؛؟]/g, " ")
    .replace(/[أإآٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/[’`]/g, "'")
    .replace(/[ı]/g, "i")
    .replace(/[İ]/g, "I")
    .replace(/[‐‑‒–—-]/g, " ")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\u0600-\u06ffa-z0-9'\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

type CurrencyCode = "USD" | "EUR" | "GBP" | "TRY";
const CURRENCY_WORDS: Partial<Record<SupportedLanguage, Record<CurrencyCode, string[]>>> = {
  en: { USD: ["dollar", "dollars"], EUR: ["euro", "euros"], GBP: ["pound", "pounds"], TRY: ["lira", "liras"] },
  it: { USD: ["dollaro", "dollari"], EUR: ["euro"], GBP: ["sterlina", "sterline"], TRY: ["lira", "lire"] },
  tr: { USD: ["dolar"], EUR: ["euro", "avro"], GBP: ["sterlin"], TRY: ["lira", "tl"] },
  ar: { USD: ["دولار", "دولارات", "دولارين", "دولاران", "دولارا"], EUR: ["يورو"], GBP: ["جنيه", "جنيهات"], TRY: ["ليرة", "ليرات", "ليرتين", "ليرتان"] },
};
const CURRENCY_SYMBOL_CODES: Record<string, CurrencyCode> = {
  "$": "USD", "US$": "USD", "€": "EUR", "£": "GBP", "₺": "TRY",
  USD: "USD", EUR: "EUR", GBP: "GBP", TRY: "TRY",
};
const ITALIAN_CURRENCY_NUMBER_WORDS: Record<string, number> = (() => {
  const units = ["zero", "uno", "due", "tre", "quattro", "cinque", "sei", "sette", "otto", "nove"];
  const words = [...units, "dieci", "undici", "dodici", "tredici", "quattordici", "quindici", "sedici", "diciassette", "diciotto", "diciannove"];
  const values = Object.fromEntries(words.map((word, index) => [word, index]));
  for (const [tens, stem] of [[20, "venti"], [30, "trenta"], [40, "quaranta"], [50, "cinquanta"], [60, "sessanta"], [70, "settanta"], [80, "ottanta"], [90, "novanta"]] as const) {
    values[stem] = tens;
    for (let unit = 1; unit < 10; unit += 1) {
      values[`${unit === 1 || unit === 8 ? stem.slice(0, -1) : stem}${units[unit]}`] = tens + unit;
    }
  }
  return values;
})();

function canonicalizeCurrencyFormatting(
  value: string,
  expectedText: string,
  targetLanguage: SupportedLanguage = "en",
): string {
  const words = CURRENCY_WORDS[targetLanguage];
  if (!words) {
    return value;
  }
  const hasMarker = /[$€£₺¥₹₩₽¤]|\b(?:USD|EUR|GBP|TRY)\b/i.test(value);
  if (!hasMarker && !/\d|[٠-٩۰-۹]|^\s*its\b/i.test(value) && targetLanguage !== "it") {
    return value;
  }
  const expectedTokens = canonicalizeMixedScriptSpeechText(expectedText).split(" ");
  const expectedHasCurrency = Object.values(words).flat().some((unit) => expectedTokens.includes(canonicalizeMixedScriptSpeechText(unit)));
  if (!hasMarker && !expectedHasCurrency) {
    return value;
  }
  const pickUnit = (variants: string[], singular: boolean): string => (
    variants.find((variant) => expectedTokens.includes(canonicalizeMixedScriptSpeechText(variant)))
    || variants[singular ? 0 : Math.min(1, variants.length - 1)]
  );
  const formatAmount = (amount: string, code: CurrencyCode, prefixSign = "", unitOverride?: string): string => {
    const sign = amount.startsWith("-") || prefixSign === "-" ? "negative " : "";
    let number = amount.replace(/^[+-]/, "");
    const grouping = targetLanguage === "it" || targetLanguage === "tr" ? "." : ",";
    const decimal = grouping === "." ? "," : ".";
    const grouped = new RegExp(`^\\d{1,3}(?:\\${grouping}\\d{3})+(?:\\${decimal}\\d{1,2})?$`);
    if (grouped.test(number)) {
      number = number.split(grouping).join("");
    }
    if (!/^\d+(?:[.,]\d{1,2})?$/.test(number) || (prefixSign && /^[+-]/.test(amount))) {
      return `unparsedcurrency${code.toLowerCase()} ${amount.replace(/\./g, " dot ").replace(/,/g, " comma ")}`;
    }
    const [integer, fraction = ""] = number.split(/[.,]/);
    const whole = integer.replace(/^0+(?=\d)/, "");
    const minor = Number(fraction.padEnd(2, "0"));
    const majorUnit = unitOverride || pickUnit(words[code], whole === "1");
    let result = `${sign}${whole} ${majorUnit}`;
    if (minor) {
      const variants = targetLanguage === "tr"
        ? code === "TRY" ? ["kuruş"] : ["sent"]
        : targetLanguage === "it" ? ["centesimo", "centesimi"]
          : targetLanguage === "ar" ? ["سنت", "سنتات", "سنتا"]
            : code === "GBP" ? ["penny", "pence"] : ["cent", "cents"];
      result += ` ${minor} ${pickUnit(variants, minor === 1)}`;
    }
    return result;
  };
  let text = canonicalizeArabicDigits(value);
  if (targetLanguage === "en" && expectedHasCurrency && /^\s*it(?:['’]s|\s+is)\b/i.test(expectedText)) {
    text = text.replace(/^\s*its\b/i, "it's");
  }
  text = text.replace(/([+-]?)\s*(US\$|[$€£₺])\s*([+-]?\d+(?:[.,]\d+)*)/gi, (_match, sign: string, symbol: string, amount: string) => (
    ` ${formatAmount(amount, CURRENCY_SYMBOL_CODES[symbol.toUpperCase()], sign)} `
  ));
  text = text.replace(/(^|[^A-Za-z0-9])([+-]?)\s*(USD|EUR|GBP|TRY)\s*([+-]?\d+(?:[.,]\d+)*)/gi, (_match, boundary: string, sign: string, code: string, amount: string) => (
    `${boundary === "-" || boundary === "+" ? " " : boundary}${formatAmount(amount, code.toUpperCase() as CurrencyCode, sign || (boundary === "-" ? "-" : ""))} `
  ));
  text = text.replace(/([+-]?\d+(?:[.,]\d+)*)\s*(US\$|USD\b|EUR\b|GBP\b|TRY\b|[$€£₺])/gi, (_match, amount: string, symbol: string) => (
    ` ${formatAmount(amount, CURRENCY_SYMBOL_CODES[symbol.toUpperCase()])} `
  ));
  for (const code of Object.keys(words) as CurrencyCode[]) {
    const variants = words[code].map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((left, right) => right.length - left.length);
    const pattern = new RegExp(`(^|\\s)([+-]?\\d+(?:[.,]\\d+)*)\\s*(${variants.join("|")})(?=$|[\\s.,!?؛؟])`, "gi");
    text = text.replace(pattern, (_match, boundary: string, amount: string, unit: string) => `${boundary}${formatAmount(amount, code, "", unit)}`);
  }
  // Never silently discard an unsupported or unattached currency symbol.
  text = text.replace(/[$€£₺¥₹₩₽¤]/g, (symbol) => ` currency${symbol.codePointAt(0)} `);
  if (targetLanguage === "it") {
    const tokens = canonicalizeMixedScriptSpeechText(text).split(" ");
    const currencyUnits = new Set(Object.values(words).flat().map((word) => canonicalizeMixedScriptSpeechText(word)));
    text = tokens.map((token, index) => (
      currencyUnits.has(tokens[index + 1]) && Object.prototype.hasOwnProperty.call(ITALIAN_CURRENCY_NUMBER_WORDS, token)
        ? String(ITALIAN_CURRENCY_NUMBER_WORDS[token]) : token
    )).join(" ");
  }
  return text;
}

function canonicalizeArabicTokenForEquivalence(value: string): string {
  const normalized = canonicalizeArabicText(value);
  if (!normalized) {
    return "";
  }
  return normalized.replace(/ة$/g, "ه");
}

function isArabicSpeechTarget(targetLanguage?: SupportedLanguage): boolean {
  return String(targetLanguage || "").trim().toLowerCase() === "ar";
}

function areArabicSingleTokenSttVariants(recognizedToken: string, expectedToken: string): boolean {
  const recognized = canonicalizeArabicTokenForEquivalence(recognizedToken);
  const expected = canonicalizeArabicTokenForEquivalence(expectedToken);
  if (!recognized || !expected || recognized === expected) {
    return recognized === expected;
  }
  if (recognized.length < 4 || expected.length < 4) {
    return false;
  }
  if (recognized === `${expected}ن` || expected === `${recognized}ن`) {
    return true;
  }
  return false;
}

function canonicalizeArabicTokenForNumber(value: string): string {
  return canonicalizeArabicText(value)
    .replace(/ة\b/g, "ه")
    .replace(/ة$/g, "ه");
}

function stripArabicDefiniteArticle(value: string): string {
  return value.startsWith("ال") && value.length > 2 ? value.slice(2) : value;
}

function stripArabicConjunction(value: string): string {
  return value.startsWith("و") && value.length > 1 ? value.slice(1) : value;
}

function arabicNumberLookupVariants(token: string): string[] {
  const canonical = canonicalizeArabicTokenForNumber(token);
  if (!canonical) {
    return [];
  }
  const withoutConjunction = stripArabicConjunction(canonical);
  const variants = new Set([
    canonical,
    stripArabicDefiniteArticle(canonical),
    withoutConjunction,
    stripArabicDefiniteArticle(withoutConjunction),
  ]);
  return Array.from(variants).filter((variant) => variant.length > 0);
}

function lookupArabicUnitNumber(token: string): number | null {
  for (const variant of arabicNumberLookupVariants(token)) {
    if (Object.prototype.hasOwnProperty.call(ARABIC_UNIT_NUMBER_WORDS, variant)) {
      return ARABIC_UNIT_NUMBER_WORDS[variant];
    }
    if (Object.prototype.hasOwnProperty.call(ARABIC_ORDINAL_NUMBER_WORDS, variant)) {
      return ARABIC_ORDINAL_NUMBER_WORDS[variant];
    }
  }
  return null;
}

function lookupArabicTensNumber(token: string): number | null {
  for (const variant of arabicNumberLookupVariants(token)) {
    if (Object.prototype.hasOwnProperty.call(ARABIC_TENS_NUMBER_WORDS, variant)) {
      return ARABIC_TENS_NUMBER_WORDS[variant];
    }
  }
  return null;
}

function lookupArabicDirectNumber(token: string): number | null {
  for (const variant of arabicNumberLookupVariants(token)) {
    if (Object.prototype.hasOwnProperty.call(ARABIC_DIRECT_NUMBER_WORDS, variant)) {
      return ARABIC_DIRECT_NUMBER_WORDS[variant];
    }
    if (Object.prototype.hasOwnProperty.call(ARABIC_UNIT_NUMBER_WORDS, variant)) {
      return ARABIC_UNIT_NUMBER_WORDS[variant];
    }
    if (Object.prototype.hasOwnProperty.call(ARABIC_ORDINAL_NUMBER_WORDS, variant)) {
      return ARABIC_ORDINAL_NUMBER_WORDS[variant];
    }
    if (Object.prototype.hasOwnProperty.call(ARABIC_TENS_NUMBER_WORDS, variant)) {
      return ARABIC_TENS_NUMBER_WORDS[variant];
    }
  }
  return null;
}

function lookupArabicTeenPrefix(token: string): number | null {
  for (const variant of arabicNumberLookupVariants(token)) {
    if (Object.prototype.hasOwnProperty.call(ARABIC_TEEN_PREFIXES, variant)) {
      return ARABIC_TEEN_PREFIXES[variant];
    }
  }
  return null;
}

function isArabicTenWord(token: string): boolean {
  return arabicNumberLookupVariants(token).some((variant) => ARABIC_TEN_WORDS.has(variant));
}

function parseArabicDigitToken(token: string): number | null {
  const canonical = canonicalizeArabicDigits(String(token || ""));
  if (!/^\d+$/.test(canonical)) {
    return null;
  }
  return Number(canonical);
}

function isZeroOnlyArabicDigitToken(token: string): boolean {
  const value = canonicalizeArabicDigits(String(token || ""));
  return /^0+$/.test(value);
}

function consumeArabicNumberExpression(
  tokens: string[],
  startIndex: number,
): { endIndex: number; value: number } | null {
  const digitValue = parseArabicDigitToken(tokens[startIndex]);
  if (digitValue !== null) {
    return {
      endIndex: startIndex + 1,
      value: digitValue,
    };
  }

  const teenPrefixValue = lookupArabicTeenPrefix(tokens[startIndex]);
  if (teenPrefixValue !== null && isArabicTenWord(tokens[startIndex + 1] || "")) {
    return {
      endIndex: startIndex + 2,
      value: teenPrefixValue,
    };
  }

  const unitValue = lookupArabicUnitNumber(tokens[startIndex]);
  const nextTensValue = lookupArabicTensNumber(tokens[startIndex + 1] || "");
  if (unitValue !== null && unitValue > 0 && nextTensValue !== null) {
    return {
      endIndex: startIndex + 2,
      value: nextTensValue + unitValue,
    };
  }

  const directValue = lookupArabicDirectNumber(tokens[startIndex]);
  if (directValue !== null) {
    return {
      endIndex: startIndex + 1,
      value: directValue,
    };
  }

  return null;
}

function isArabicHourMarker(token: string): boolean {
  return ARABIC_HOUR_MARKERS.has(canonicalizeArabicTokenForNumber(token));
}

function consumeArabicHourExpression(
  tokens: string[],
  startIndex: number,
): { endIndex: number; value: number } | null {
  const parsed = consumeArabicNumberExpression(tokens, startIndex);
  if (!parsed || parsed.value < 0 || parsed.value > 24) {
    return null;
  }

  let endIndex = parsed.endIndex;
  if (isZeroOnlyArabicDigitToken(tokens[endIndex] || "")) {
    endIndex += 1;
  }

  return {
    endIndex,
    value: parsed.value,
  };
}

function canonicalizeArabicNumbers(value: string): string {
  const tokens = String(value || "").split(/\s+/).filter((token) => token.length > 0);
  if (!tokens.length) {
    return "";
  }

  const normalizedTokens: string[] = [];
  let index = 0;
  while (index < tokens.length) {
    if (/^\d{7,}$/.test(tokens[index])) {
      normalizedTokens.push(tokens[index]);
      index += 1;
      continue;
    }
    if (isArabicHourMarker(tokens[index])) {
      const hour = consumeArabicHourExpression(tokens, index + 1);
      if (hour) {
        normalizedTokens.push("الساعه", `وقت${hour.value}`);
        index = hour.endIndex;
        continue;
      }
      normalizedTokens.push("الساعه");
      index += 1;
      continue;
    }

    const number = consumeArabicNumberExpression(tokens, index);
    if (number) {
      normalizedTokens.push(String(number.value));
      index = number.endIndex;
      continue;
    }

    normalizedTokens.push(tokens[index]);
    index += 1;
  }

  return normalizedTokens.join(" ");
}

function canonicalizeEnglishContractions(value: string): string {
  let normalized = String(value || "");
  for (const [pattern, replacement] of ENGLISH_CONTRACTION_RULES) {
    normalized = normalized.replace(pattern, replacement);
  }
  return normalized;
}

function canonicalizeEnglishTokenVariants(value: string): string {
  const tokens = String(value || "")
    .split(/\s+/)
    .filter((token) => token.length > 0);
  if (!tokens.length) {
    return "";
  }
  return tokens
    .map((token) => ENGLISH_CANONICAL_TOKEN_MAP[token] || token)
    .join(" ");
}

function collapseDottedLatinInitialisms(value: string): string {
  return String(value || "").replace(/\b(?:[A-Za-z]\.){2,}/g, (match) => match.replace(/\./g, ""));
}

function expandComparableSpeechTokens(tokens: string[]): string[] {
  return tokens.flatMap((token) => LATIN_COMPARABLE_TOKEN_EXPANSIONS[token] || [token]);
}

function extractExpectedLatinInitialismTokens(expectedText: string): Set<string> {
  const initialisms = new Set<string>();
  const raw = String(expectedText || "");
  const matches = raw.match(/\b(?:[A-Z]\.){2,}|\b[A-Z]{2,}\b/g) || [];
  for (const match of matches) {
    const compact = match.replace(/\./g, "").toLowerCase();
    if (compact.length < 2 || compact === "ok") {
      continue;
    }
    initialisms.add(compact);
  }
  return initialisms;
}

function spokenLatinLetterTokenToLetterCandidates(token: string): string[] {
  const primaryLetter = LATIN_SPOKEN_LETTER_TOKEN_MAP[token] || "";
  if (!primaryLetter) {
    return [];
  }

  const candidates = new Set([primaryLetter]);
  if (token === "ay") {
    candidates.add("i");
  }
  if (token === "اي") {
    candidates.add("a");
    candidates.add("e");
  }
  return Array.from(candidates);
}

function buildSpokenLatinInitialismCandidates(tokens: string[]): string[] {
  let candidates = [""];
  for (const token of tokens) {
    const letterCandidates = spokenLatinLetterTokenToLetterCandidates(token);
    if (letterCandidates.length === 0) {
      return [];
    }
    candidates = candidates.flatMap((candidate) => (
      letterCandidates.map((letter) => `${candidate}${letter}`)
    ));
  }
  return candidates;
}

function collapseSpokenLatinInitialismsAgainstExpected(tokens: string[], expectedText: string): string[] {
  const expectedInitialisms = extractExpectedLatinInitialismTokens(expectedText);
  if (expectedInitialisms.size === 0 || tokens.length < 2) {
    return tokens;
  }

  const maxInitialismLength = Math.max(...Array.from(expectedInitialisms).map((initialism) => initialism.length));
  const collapsedTokens: string[] = [];
  let index = 0;
  while (index < tokens.length) {
    let matchedInitialism = "";
    let matchedLength = 0;
    const maxSpan = Math.min(maxInitialismLength, tokens.length - index);
    for (let spanLength = maxSpan; spanLength >= 2; spanLength -= 1) {
      const candidates = buildSpokenLatinInitialismCandidates(tokens.slice(index, index + spanLength));
      const candidate = candidates.find((value) => expectedInitialisms.has(value));
      if (candidate) {
        matchedInitialism = candidate;
        matchedLength = spanLength;
        break;
      }
    }

    if (matchedInitialism) {
      collapsedTokens.push(matchedInitialism);
      index += matchedLength;
      continue;
    }
    collapsedTokens.push(tokens[index]);
    index += 1;
  }
  return collapsedTokens;
}

function expectedTextContainsWifi(expectedText: string): boolean {
  const expectedTokens = tokenizeComparableSpeechText(expectedText);
  return expectedTokens.some((token, index) => token === "wi" && expectedTokens[index + 1] === "fi");
}

function collapseKnownLatinTermsAgainstExpected(tokens: string[], expectedText: string): string[] {
  if (!expectedTextContainsWifi(expectedText) || tokens.length < 2) {
    return tokens;
  }

  const collapsedTokens: string[] = [];
  let index = 0;
  while (index < tokens.length) {
    const current = tokens[index];
    const next = tokens[index + 1];
    if (
      (current === "vay" || current === "why")
      && (next === "fay" || next === "fi")
    ) {
      collapsedTokens.push("wi", "fi");
      index += 2;
      continue;
    }
    collapsedTokens.push(current);
    index += 1;
  }
  return collapsedTokens;
}

function buildEnglishSurfaceBaseCandidates(token: string, options?: { includeVerbLike?: boolean }): Set<string> {
  const normalized = String(token || "").toLowerCase().replace(/[^a-z']/g, "");
  const candidates = new Set<string>();
  if (!normalized || !/^[a-z']+$/.test(normalized)) {
    return candidates;
  }

  candidates.add(normalized);
  if (normalized.endsWith("'s") && normalized.length > 3) {
    candidates.add(normalized.slice(0, -2));
  }
  if (normalized.endsWith("s'") && normalized.length > 3) {
    candidates.add(normalized.slice(0, -1));
  }

  const bare = normalized.replace(/'s$/g, "").replace(/s'$/g, "s");
  if (/^[a-z]+$/.test(bare)) {
    if (bare.endsWith("ies") && bare.length >= 6) {
      candidates.add(`${bare.slice(0, -3)}y`);
    }
    if (bare.endsWith("es") && bare.length >= 6) {
      candidates.add(bare.slice(0, -2));
    }
    if (bare.endsWith("s") && !bare.endsWith("ss") && bare.length >= 6) {
      candidates.add(bare.slice(0, -1));
    }

    if (options?.includeVerbLike) {
      if (bare.endsWith("ing") && bare.length >= 7) {
        const stem = bare.slice(0, -3);
        candidates.add(stem);
        if (/([bcdfghjklmnpqrstvwxyz])\1$/.test(stem)) {
          candidates.add(stem.slice(0, -1));
        }
        candidates.add(`${stem}e`);
      }
      if (bare.endsWith("ied") && bare.length >= 6) {
        candidates.add(`${bare.slice(0, -3)}y`);
      }
      if (bare.endsWith("ed") && bare.length >= 6) {
        const stem = bare.slice(0, -2);
        candidates.add(stem);
        if (/([bcdfghjklmnpqrstvwxyz])\1$/.test(stem)) {
          candidates.add(stem.slice(0, -1));
        }
        candidates.add(`${stem}e`);
      }
    }
  }

  return candidates;
}

function areEnglishSurfaceVariants(
  recognizedToken: string,
  expectedToken: string,
  options?: { includeVerbLike?: boolean },
): boolean {
  if (!/^[a-z']+$/.test(recognizedToken) || !/^[a-z']+$/.test(expectedToken)) {
    return false;
  }
  const recognizedBases = buildEnglishSurfaceBaseCandidates(recognizedToken, options);
  const expectedBases = buildEnglishSurfaceBaseCandidates(expectedToken, options);
  for (const recognizedBase of recognizedBases) {
    if (expectedBases.has(recognizedBase)) {
      return true;
    }
  }
  return false;
}

function areFuzzyEnglishSingleTokenSttMatches(recognizedToken: string, expectedToken: string): boolean {
  if (!/^[a-z]+$/.test(recognizedToken) || !/^[a-z]+$/.test(expectedToken)) {
    return false;
  }
  if (areEnglishSurfaceVariants(recognizedToken, expectedToken, { includeVerbLike: true })) {
    return false;
  }
  const maxLen = Math.max(recognizedToken.length, expectedToken.length);
  const minLen = Math.min(recognizedToken.length, expectedToken.length);
  if (minLen < 5) {
    return false;
  }
  const distance = levenshteinDistance(recognizedToken, expectedToken);
  const maxDistance = maxLen >= 7 ? 2 : 1;
  const similarity = 1 - distance / Math.max(maxLen, 1);
  return distance <= maxDistance && similarity >= (maxLen >= 7 ? 0.74 : 0.82);
}

function areSingleTokenSpeechVariants(recognizedToken: string, expectedToken: string): boolean {
  return areFuzzyEnglishSingleTokenSttMatches(recognizedToken, expectedToken);
}

function isEnglishSpeechTarget(targetLanguage?: SupportedLanguage): boolean {
  return String(targetLanguage || "").trim().toLowerCase() === "en";
}

function canonicalizeEnglishExpectedTokenAlias(value: string): string {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isExpectedProperNameSttAlias(
  recognizedToken: string,
  expectedToken: string,
): boolean {
  const expected = canonicalizeEnglishExpectedTokenAlias(expectedToken);
  const aliases = ENGLISH_EXPECTED_TOKEN_STT_ALIASES[expected];
  if (!aliases || aliases.length === 0) {
    return false;
  }
  const recognized = canonicalizeEnglishExpectedTokenAlias(recognizedToken);
  return aliases.includes(recognized);
}

function isEnglishExpectedTokenSttAlias(
  recognizedToken: string,
  expectedToken: string,
  targetLanguage?: SupportedLanguage,
): boolean {
  if (!isEnglishSpeechTarget(targetLanguage)) {
    return false;
  }
  return isExpectedProperNameSttAlias(recognizedToken, expectedToken);
}

function consumeEnglishNumberWords(tokens: string[], startIndex: number): { endIndex: number; value: number } | null {
  let index = startIndex;
  let total = 0;
  let current = 0;
  let consumed = 0;
  let usedWord = false;
  let lastKind: "start" | "unit" | "teen" | "tens" | "hundred" | "scale" = "start";
  let hundredSeen = false;
  let lastScale = Infinity;

  while (index < tokens.length) {
    const token = tokens[index];
    const following = tokens[index + 1];
    if (token === "and" && (lastKind === "hundred" || lastKind === "scale") && (
      Object.prototype.hasOwnProperty.call(ENGLISH_NUMBER_UNITS, following)
      || Object.prototype.hasOwnProperty.call(ENGLISH_NUMBER_TEENS, following)
      || Object.prototype.hasOwnProperty.call(ENGLISH_NUMBER_TENS, following)
    )) {
      index += 1;
      consumed += 1;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(ENGLISH_NUMBER_UNITS, token)) {
      if (lastKind === "unit" || lastKind === "teen" || (ENGLISH_NUMBER_UNITS[token] === 0 && lastKind !== "start")) {
        break;
      }
      current += ENGLISH_NUMBER_UNITS[token];
      index += 1;
      consumed += 1;
      usedWord = true;
      lastKind = "unit";
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(ENGLISH_NUMBER_TEENS, token)) {
      if (lastKind === "unit" || lastKind === "teen" || lastKind === "tens") {
        break;
      }
      current += ENGLISH_NUMBER_TEENS[token];
      index += 1;
      consumed += 1;
      usedWord = true;
      lastKind = "teen";
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(ENGLISH_NUMBER_TENS, token)) {
      if (lastKind === "unit" || lastKind === "teen" || lastKind === "tens") {
        break;
      }
      current += ENGLISH_NUMBER_TENS[token];
      index += 1;
      consumed += 1;
      usedWord = true;
      lastKind = "tens";
      continue;
    }
    if (token === "hundred") {
      if (hundredSeen || (lastKind === "unit" && current === 0) || (lastKind !== "unit" && lastKind !== "start" && lastKind !== "scale")) {
        break;
      }
      if (current === 0) {
        current = 1;
      }
      current *= 100;
      index += 1;
      consumed += 1;
      usedWord = true;
      lastKind = "hundred";
      hundredSeen = true;
      continue;
    }
    const scale = token === "thousand" ? 1000 : token === "million" ? 1000000 : 0;
    if (scale && scale < lastScale && !(lastKind === "unit" && current === 0)) {
      total += Math.max(current, 1) * scale;
      current = 0;
      lastScale = scale;
      lastKind = "scale";
      hundredSeen = false;
      index += 1;
      consumed += 1;
      usedWord = true;
      continue;
    }
    break;
  }

  if (!usedWord || consumed === 0) {
    return null;
  }
  return { endIndex: index, value: total + current };
}

function canonicalizeEnglishNumbers(value: string): string {
  const tokens = String(value || "").split(/\s+/).filter((token) => token.length > 0);
  if (!tokens.length) {
    return "";
  }

  const normalizedTokens: string[] = [];
  let index = 0;
  while (index < tokens.length) {
    const token = tokens[index];
    if (/^\d+$/.test(token)) {
      normalizedTokens.push(token.length >= 7 ? token : String(Number(token)));
      index += 1;
      continue;
    }
    const parsed = consumeEnglishNumberWords(tokens, index);
    if (parsed) {
      normalizedTokens.push(String(parsed.value));
      index = parsed.endIndex;
      continue;
    }
    normalizedTokens.push(token);
    index += 1;
  }
  return normalizedTokens.join(" ");
}

function collapseLongDigitSequences(value: string): string {
  return String(value || "").replace(
    /\d(?:[\s()./-]*\d){6,}/g,
    (match) => match.replace(/\D/g, ""),
  );
}

function parseTurkishNumberToken(
  token: string,
): {
  value: number;
  kind: "digits" | "unit" | "tens" | "hundred" | "thousand";
  suffix: string;
} | null {
  const normalized = String(token || "").replace(/'/g, "");
  if (!normalized) {
    return null;
  }

  const digitMatch = normalized.match(/^(\d+)([a-z]+)?$/);
  if (digitMatch) {
    return {
      value: Number(digitMatch[1]),
      kind: "digits",
      suffix: digitMatch[2] || "",
    };
  }

  for (const root of TURKISH_NUMBER_ROOTS) {
    if (normalized === root) {
      if (root === "yuz") {
        return { value: 100, kind: "hundred", suffix: "" };
      }
      if (root === "bin") {
        return { value: 1000, kind: "thousand", suffix: "" };
      }
      if (Object.prototype.hasOwnProperty.call(TURKISH_NUMBER_TENS, root)) {
        return { value: TURKISH_NUMBER_TENS[root], kind: "tens", suffix: "" };
      }
      return { value: TURKISH_NUMBER_UNITS[root], kind: "unit", suffix: "" };
    }

    if (!normalized.startsWith(root)) {
      continue;
    }

    const suffix = normalized.slice(root.length);
    if (suffix.length < 2 || !/^[a-z]+$/.test(suffix)) {
      continue;
    }

    if (root === "yuz") {
      return { value: 100, kind: "hundred", suffix };
    }
    if (root === "bin") {
      return { value: 1000, kind: "thousand", suffix };
    }
    if (Object.prototype.hasOwnProperty.call(TURKISH_NUMBER_TENS, root)) {
      return { value: TURKISH_NUMBER_TENS[root], kind: "tens", suffix };
    }
    return { value: TURKISH_NUMBER_UNITS[root], kind: "unit", suffix };
  }

  return null;
}

function consumeTurkishHourExpression(
  tokens: string[],
  startIndex: number,
): { endIndex: number; value: string } | null {
  const current = parseTurkishNumberToken(tokens[startIndex]);
  if (!current || current.kind !== "digits" || current.suffix) {
    return null;
  }

  const next = parseTurkishNumberToken(tokens[startIndex + 1]);
  if (next && next.kind === "digits" && next.value === 0) {
    if (next.suffix) {
      return {
        endIndex: startIndex + 2,
        value: `${current.value}${next.suffix}`,
      };
    }
    const trailingSuffix = String(tokens[startIndex + 2] || "").replace(/'/g, "");
    if (/^[a-z]{2,}$/.test(trailingSuffix)) {
      return {
        endIndex: startIndex + 3,
        value: `${current.value}${trailingSuffix}`,
      };
    }
  }

  return null;
}

function consumeTurkishNumberWords(tokens: string[], startIndex: number): { endIndex: number; value: string } | null {
  const hourExpression = consumeTurkishHourExpression(tokens, startIndex);
  if (hourExpression) {
    return hourExpression;
  }

  let index = startIndex;
  let total = 0;
  let current = 0;
  let consumed = 0;
  let trailingSuffix = "";

  while (index < tokens.length) {
    const parsed = parseTurkishNumberToken(tokens[index]);
    if (!parsed) {
      break;
    }

    consumed += 1;
    index += 1;

    if (parsed.kind === "digits") {
      if (consumed > 1) {
        index -= 1;
        consumed -= 1;
        break;
      }
      total = parsed.value;
      trailingSuffix = parsed.suffix;
      break;
    }

    if (parsed.kind === "unit" || parsed.kind === "tens") {
      current += parsed.value;
    } else if (parsed.kind === "hundred") {
      current = Math.max(current, 1) * parsed.value;
    } else if (parsed.kind === "thousand") {
      total += Math.max(current, 1) * parsed.value;
      current = 0;
    }

    if (parsed.suffix) {
      trailingSuffix = parsed.suffix;
      break;
    }
  }

  if (consumed === 0) {
    return null;
  }

  return {
    endIndex: index,
    value: `${total + current}${trailingSuffix}`,
  };
}

function canonicalizeTurkishNumbers(value: string): string {
  const tokens = String(value || "").split(/\s+/).filter((token) => token.length > 0);
  if (!tokens.length) {
    return "";
  }

  const normalizedTokens: string[] = [];
  let index = 0;
  while (index < tokens.length) {
    if (/^\d{7,}$/.test(tokens[index])) {
      normalizedTokens.push(tokens[index]);
      index += 1;
      continue;
    }
    const parsed = consumeTurkishNumberWords(tokens, index);
    if (!parsed) {
      normalizedTokens.push(tokens[index].replace(/'/g, ""));
      index += 1;
      continue;
    }
    normalizedTokens.push(parsed.value);
    index = parsed.endIndex;
  }

  return normalizedTokens.join(" ");
}

export function tokenizeComparableSpeechText(value: string): string[] {
  const mixedScriptText = collapseLongDigitSequences(canonicalizeMixedScriptSpeechText(value));
  const normalized = collapseLongDigitSequences(canonicalizeEnglishTokenVariants(
    canonicalizeTurkishNumbers(
      canonicalizeEnglishNumbers(
        canonicalizeArabicNumbers(canonicalizeEnglishContractions(mixedScriptText)),
      ),
    ),
  ))
    .replace(/[^\u0600-\u06ffa-z0-9'\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) {
    return [];
  }

  return expandComparableSpeechTokens(
    normalized.split(" ").filter((token) => token.length > 0),
  );
}

export function normalizeComparableSpeechText(value: string): string {
  return tokenizeComparableSpeechText(value).join(" ");
}

function tokenize(value: string): string[] {
  return tokenizeComparableSpeechText(value);
}

function areExactTokenListsEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

interface SpeechTokenEquivalenceOptions {
  allowSingleTokenSttVariants?: boolean;
  allowProperNameSttVariants?: boolean;
  targetLanguage?: SupportedLanguage;
}

function areLatinProperNameSingleTokenSttVariants(recognizedToken: string, expectedToken: string): boolean {
  const recognized = canonicalizeEnglishExpectedTokenAlias(recognizedToken);
  const expected = canonicalizeEnglishExpectedTokenAlias(expectedToken);
  if (!recognized || !expected) {
    return false;
  }
  if (recognized === expected) {
    return true;
  }
  if (!/^[a-z]+$/.test(recognized) || !/^[a-z]+$/.test(expected)) {
    return false;
  }
  if (recognized.length < 4 || expected.length < 4) {
    return false;
  }
  if (recognized.charAt(0) !== expected.charAt(0)) {
    return false;
  }

  const maxLen = Math.max(recognized.length, expected.length);
  const distance = levenshteinDistance(recognized, expected);
  const maxDistance = maxLen >= 8 ? 2 : 1;
  const similarity = 1 - distance / Math.max(maxLen, 1);
  return distance <= maxDistance && similarity >= (maxLen >= 8 ? 0.76 : 0.78);
}

function areProperNameSpeechVariants(recognizedToken: string, expectedToken: string): boolean {
  return isExpectedProperNameSttAlias(recognizedToken, expectedToken)
    || areLatinProperNameSingleTokenSttVariants(recognizedToken, expectedToken);
}

function areComparableSpeechTokensEquivalent(
  recognizedToken: string,
  expectedToken: string,
  options?: SpeechTokenEquivalenceOptions,
): boolean {
  if (recognizedToken === expectedToken) {
    return true;
  }

  if (options?.allowProperNameSttVariants && areProperNameSpeechVariants(recognizedToken, expectedToken)) {
    return true;
  }

  if (isEnglishSpeechTarget(options?.targetLanguage)) {
    const recognizedAliases = ENGLISH_HOMOPHONE_TOKEN_EQUIVALENTS[recognizedToken] || [];
    if (recognizedAliases.includes(expectedToken)) {
      return true;
    }
    const expectedAliases = ENGLISH_HOMOPHONE_TOKEN_EQUIVALENTS[expectedToken] || [];
    if (expectedAliases.includes(recognizedToken)) {
      return true;
    }
    if (isEnglishExpectedTokenSttAlias(recognizedToken, expectedToken, options?.targetLanguage)) {
      return true;
    }
  }

  if (containsArabicScript(recognizedToken) || containsArabicScript(expectedToken)) {
    if (
      canonicalizeArabicTokenForEquivalence(recognizedToken)
      === canonicalizeArabicTokenForEquivalence(expectedToken)
    ) {
      return true;
    }
  }

  if (isEnglishSpeechTarget(options?.targetLanguage) && areEnglishSurfaceVariants(recognizedToken, expectedToken)) {
    return true;
  }

  if (options?.allowSingleTokenSttVariants && isEnglishSpeechTarget(options?.targetLanguage)) {
    if (areSingleTokenSpeechVariants(recognizedToken, expectedToken)) {
      return true;
    }
  }

  if (options?.allowSingleTokenSttVariants && isArabicSpeechTarget(options?.targetLanguage)) {
    if (areArabicSingleTokenSttVariants(recognizedToken, expectedToken)) {
      return true;
    }
  }

  return false;
}

function areComparableSpeechTokensPassEquivalent(
  recognizedToken: string,
  expectedToken: string,
  options?: SpeechTokenEquivalenceOptions,
): boolean {
  if (recognizedToken === expectedToken) {
    return true;
  }

  if (options?.allowProperNameSttVariants && areProperNameSpeechVariants(recognizedToken, expectedToken)) {
    return true;
  }

  if (isEnglishSpeechTarget(options?.targetLanguage)) {
    const recognizedAliases = ENGLISH_HOMOPHONE_TOKEN_EQUIVALENTS[recognizedToken] || [];
    if (recognizedAliases.includes(expectedToken)) {
      return true;
    }
    const expectedAliases = ENGLISH_HOMOPHONE_TOKEN_EQUIVALENTS[expectedToken] || [];
    if (expectedAliases.includes(recognizedToken)) {
      return true;
    }
    if (isEnglishExpectedTokenSttAlias(recognizedToken, expectedToken, options?.targetLanguage)) {
      return true;
    }
  }

  if (containsArabicScript(recognizedToken) || containsArabicScript(expectedToken)) {
    if (
      canonicalizeArabicTokenForEquivalence(recognizedToken)
      === canonicalizeArabicTokenForEquivalence(expectedToken)
    ) {
      return true;
    }
  }

  if (options?.allowSingleTokenSttVariants && isEnglishSpeechTarget(options?.targetLanguage)) {
    if (areSingleTokenSpeechVariants(recognizedToken, expectedToken)) {
      return true;
    }
  }

  if (options?.allowSingleTokenSttVariants && isArabicSpeechTarget(options?.targetLanguage)) {
    if (areArabicSingleTokenSttVariants(recognizedToken, expectedToken)) {
      return true;
    }
  }

  return false;
}

interface DisplaySpeechToken {
  text: string;
  start: number;
  end: number;
  comparableTokens: string[];
}

interface DisplaySpeechReplacement {
  start: number;
  end: number;
  text: string;
}

function normalizeDisplayProperNameKey(value: string): string {
  const normalized = normalizeComparableSpeechText(value).replace(/\s+/g, "");
  if (!normalized) {
    return "";
  }
  return containsArabicScript(normalized)
    ? canonicalizeArabicTokenForEquivalence(normalized)
    : normalized;
}

function extractDisplaySpeechTokens(value: string): DisplaySpeechToken[] {
  const raw = String(value || "");
  return Array.from(raw.matchAll(SPEECH_DISPLAY_TOKEN_PATTERN))
    .map((match) => {
      const text = match[0] || "";
      const start = typeof match.index === "number" ? match.index : 0;
      return {
        text,
        start,
        end: start + text.length,
        comparableTokens: tokenizeComparableSpeechText(text),
      };
    })
    .filter((token) => token.text.length > 0);
}

function isDisplayTokenAtSentenceStart(fullText: string, tokenStart: number): boolean {
  const text = String(fullText || "");
  for (let index = tokenStart - 1; index >= 0; index -= 1) {
    const character = text[index];
    if (/\s/.test(character)) {
      continue;
    }
    return /[.!?؟]/.test(character);
  }
  return true;
}

function hasDisplayProperNameCasing(value: string): boolean {
  const text = String(value || "");
  return /^[A-ZÀ-ÖØ-ÞİIŞĞÜÖÇ]/.test(text) && /[a-zà-öø-ÿışğüöç]/.test(text);
}

function isExpectedDisplayProperNameToken(expectedText: string, token: DisplaySpeechToken): boolean {
  if (token.comparableTokens.length !== 1) {
    return false;
  }

  const key = normalizeDisplayProperNameKey(token.text);
  if (!key || key.length < 2 || /\d/.test(key)) {
    return false;
  }

  if (AUTHORED_PROPER_NAME_DISPLAY_TOKENS.has(key) || Boolean(ENGLISH_EXPECTED_TOKEN_STT_ALIASES[key])) {
    return true;
  }

  if (containsArabicScript(token.text)) {
    return false;
  }
  if (!hasDisplayProperNameCasing(token.text) || PROPER_NAME_DISPLAY_STOPWORDS.has(key)) {
    return false;
  }

  return !isDisplayTokenAtSentenceStart(expectedText, token.start);
}

function buildExpectedProperNameComparableTokenSet(expectedText: string): Set<string> {
  const tokens = extractDisplaySpeechTokens(expectedText)
    .filter((token) => isExpectedDisplayProperNameToken(expectedText, token));
  return new Set(
    tokens
      .flatMap((token) => token.comparableTokens)
      .filter((token) => token.length > 0),
  );
}

function areRecognizedDisplayTokensEquivalentToExpectedName(
  recognizedTokens: DisplaySpeechToken[],
  expectedToken: DisplaySpeechToken,
  targetLanguage?: SupportedLanguage,
): boolean {
  const expectedComparableToken = expectedToken.comparableTokens[0] || "";
  const recognizedComparableTokens = recognizedTokens.flatMap((token) => token.comparableTokens);
  if (!expectedComparableToken || recognizedComparableTokens.length === 0) {
    return false;
  }

  if (recognizedComparableTokens.length === 1) {
    return areComparableSpeechTokensPassEquivalent(
      recognizedComparableTokens[0],
      expectedComparableToken,
      {
        allowSingleTokenSttVariants: false,
        allowProperNameSttVariants: true,
        targetLanguage,
      },
    );
  }

  return areJoinedSingleTokenFragmentsEquivalent(
    recognizedComparableTokens,
    expectedComparableToken,
    targetLanguage,
  );
}

function findDisplayReplacementForExpectedName(params: {
  recognizedTokens: DisplaySpeechToken[];
  usedRecognizedTokens: boolean[];
  expectedToken: DisplaySpeechToken;
  searchStartIndex: number;
  targetLanguage?: SupportedLanguage;
}): { startIndex: number; endIndex: number } | null {
  const {
    recognizedTokens,
    usedRecognizedTokens,
    expectedToken,
    searchStartIndex,
    targetLanguage,
  } = params;
  const maxSpanLength = 4;

  // Prefer a complete name token before considering split recognition fragments.
  for (let index = searchStartIndex; index < recognizedTokens.length; index += 1) {
    if (!usedRecognizedTokens[index] && areRecognizedDisplayTokensEquivalentToExpectedName(
      [recognizedTokens[index]], expectedToken, targetLanguage,
    )) return { startIndex: index, endIndex: index + 1 };
  }

  for (let startIndex = searchStartIndex; startIndex < recognizedTokens.length; startIndex += 1) {
    if (usedRecognizedTokens[startIndex]) {
      continue;
    }
    const maxEndIndex = Math.min(recognizedTokens.length, startIndex + maxSpanLength);
    for (let endIndex = startIndex + 1; endIndex <= maxEndIndex; endIndex += 1) {
      const spanUsed = usedRecognizedTokens.slice(startIndex, endIndex).some(Boolean);
      if (spanUsed) {
        break;
      }
      const recognizedSpan = recognizedTokens.slice(startIndex, endIndex);
      if (!areRecognizedDisplayTokensEquivalentToExpectedName(recognizedSpan, expectedToken, targetLanguage)) {
        continue;
      }
      return { startIndex, endIndex };
    }
  }

  return null;
}

function buildDisplayRecognizedText(
  recognizedText: string,
  expectedText: string,
  targetLanguage?: SupportedLanguage,
): string {
  const rawRecognizedText = String(recognizedText || "");
  if (!rawRecognizedText.trim() || !String(expectedText || "").trim()) {
    return rawRecognizedText;
  }

  const expectedNameTokens = extractDisplaySpeechTokens(expectedText)
    .filter((token) => isExpectedDisplayProperNameToken(expectedText, token));
  const recognizedTokens = extractDisplaySpeechTokens(rawRecognizedText);
  if (expectedNameTokens.length === 0 || recognizedTokens.length === 0) {
    return rawRecognizedText;
  }

  const replacements: DisplaySpeechReplacement[] = [];
  const usedRecognizedTokens = new Array(recognizedTokens.length).fill(false);
  let searchStartIndex = 0;
  for (const expectedToken of expectedNameTokens) {
    const match = findDisplayReplacementForExpectedName({
      recognizedTokens,
      usedRecognizedTokens,
      expectedToken,
      searchStartIndex,
      targetLanguage,
    });
    if (!match) {
      continue;
    }

    for (let index = match.startIndex; index < match.endIndex; index += 1) {
      usedRecognizedTokens[index] = true;
    }
    searchStartIndex = match.endIndex;
    replacements.push({
      start: recognizedTokens[match.startIndex].start,
      end: recognizedTokens[match.endIndex - 1].end,
      text: expectedToken.text,
    });
  }

  if (replacements.length === 0) {
    return rawRecognizedText;
  }

  let displayText = "";
  let cursor = 0;
  for (const replacement of replacements.sort((left, right) => left.start - right.start)) {
    displayText += rawRecognizedText.slice(cursor, replacement.start);
    displayText += replacement.text;
    cursor = replacement.end;
  }
  displayText += rawRecognizedText.slice(cursor);
  return displayText;
}

function tokenSequenceEditDistance(left: string[], right: string[]): number {
  if (left.length === 0) {
    return right.length;
  }
  if (right.length === 0) {
    return left.length;
  }

  const matrix: number[][] = Array.from({ length: left.length + 1 }, (_unusedRow, rowIndex) =>
    Array.from({ length: right.length + 1 }, (_unusedColumn, columnIndex) => {
      if (rowIndex === 0) {
        return columnIndex;
      }
      if (columnIndex === 0) {
        return rowIndex;
      }
      return 0;
    }),
  );

  for (let rowIndex = 1; rowIndex <= left.length; rowIndex += 1) {
    for (let columnIndex = 1; columnIndex <= right.length; columnIndex += 1) {
      const substitutionCost = left[rowIndex - 1] === right[columnIndex - 1] ? 0 : 1;
      matrix[rowIndex][columnIndex] = Math.min(
        matrix[rowIndex - 1][columnIndex] + 1,
        matrix[rowIndex][columnIndex - 1] + 1,
        matrix[rowIndex - 1][columnIndex - 1] + substitutionCost,
      );
    }
  }

  return matrix[left.length][right.length];
}

function collapseAdjacentRepeatedComparableTokens(
  recognizedTokens: string[],
  expectedTokens: string[],
): string[] {
  let nextTokens = [...recognizedTokens];
  let changed = true;

  while (changed) {
    changed = false;
    const currentDistance = expectedTokens.length > 0
      ? tokenSequenceEditDistance(nextTokens, expectedTokens)
      : Number.POSITIVE_INFINITY;

    for (
      let spanLength = Math.min(4, Math.floor(nextTokens.length / 2));
      spanLength >= 1 && !changed;
      spanLength -= 1
    ) {
      for (let startIndex = 0; startIndex + (spanLength * 2) <= nextTokens.length; startIndex += 1) {
        const firstSpan = nextTokens.slice(startIndex, startIndex + spanLength);
        const secondSpan = nextTokens.slice(startIndex + spanLength, startIndex + (spanLength * 2));
        if (!areExactTokenListsEqual(firstSpan, secondSpan)) {
          continue;
        }

        const candidateTokens = [
          ...nextTokens.slice(0, startIndex + spanLength),
          ...nextTokens.slice(startIndex + (spanLength * 2)),
        ];
        const candidateDistance = expectedTokens.length > 0
          ? tokenSequenceEditDistance(candidateTokens, expectedTokens)
          : currentDistance;
        if (candidateDistance > currentDistance) {
          continue;
        }

        nextTokens = candidateTokens;
        changed = true;
        break;
      }
    }
  }

  return nextTokens;
}

function areJoinedSingleTokenFragmentsEquivalent(
  recognizedFragments: string[],
  expectedToken: string,
  targetLanguage?: SupportedLanguage,
  expectedProperNameTokens: Set<string> = new Set(),
): boolean {
  const joinedToken = recognizedFragments.join("");
  if (!joinedToken || joinedToken === recognizedFragments.join(" ")) {
    return false;
  }
  return areComparableSpeechTokensPassEquivalent(joinedToken, expectedToken, {
    allowSingleTokenSttVariants: true,
    allowProperNameSttVariants: expectedProperNameTokens.has(expectedToken),
    targetLanguage,
  });
}

function mergeRecognizedFragmentsAgainstExpected(
  recognizedTokens: string[],
  expectedTokens: string[],
  targetLanguage?: SupportedLanguage,
  expectedProperNameTokens: Set<string> = new Set(),
): string[] {
  if (
    recognizedTokens.length <= expectedTokens.length
    || recognizedTokens.length === 0
    || expectedTokens.length === 0
  ) {
    return recognizedTokens;
  }

  const mergedTokens: string[] = [];
  let recognizedIndex = 0;
  for (let expectedIndex = 0; expectedIndex < expectedTokens.length && recognizedIndex < recognizedTokens.length; expectedIndex += 1) {
    const expectedToken = expectedTokens[expectedIndex];
    const remainingExpectedAfterThis = expectedTokens.length - expectedIndex - 1;
    const maxSpan = Math.min(
      4,
      recognizedTokens.length - recognizedIndex - remainingExpectedAfterThis,
    );
    let matchedSpan = 0;
    for (let spanLength = maxSpan; spanLength >= 2; spanLength -= 1) {
      const fragments = recognizedTokens.slice(recognizedIndex, recognizedIndex + spanLength);
      if (!areJoinedSingleTokenFragmentsEquivalent(
        fragments,
        expectedToken,
        targetLanguage,
        expectedProperNameTokens,
      )) {
        continue;
      }
      matchedSpan = spanLength;
      break;
    }

    if (matchedSpan > 0) {
      mergedTokens.push(expectedToken);
      recognizedIndex += matchedSpan;
      continue;
    }

    mergedTokens.push(recognizedTokens[recognizedIndex]);
    recognizedIndex += 1;
  }

  return [
    ...mergedTokens,
    ...recognizedTokens.slice(recognizedIndex),
  ];
}

function alignRecognizedComparableTokensToExpected(
  recognizedTokens: string[],
  expectedTokens: string[],
  targetLanguage?: SupportedLanguage,
  expectedProperNameTokens: Set<string> = new Set(),
): string[] {
  if (recognizedTokens.length === 0 || expectedTokens.length === 0) {
    return recognizedTokens;
  }

  const clockAdjustedTokens = stripRecognizerAddedEnglishClockQualifiers(
    recognizedTokens,
    expectedTokens,
    targetLanguage,
  );
  const collapsedTokens = collapseAdjacentRepeatedComparableTokens(clockAdjustedTokens, expectedTokens);
  const fragmentMergedTokens = mergeRecognizedFragmentsAgainstExpected(
    collapsedTokens,
    expectedTokens,
    targetLanguage,
    expectedProperNameTokens,
  );
  if (!areExactTokenListsEqual(fragmentMergedTokens, collapsedTokens)) {
    return alignRecognizedComparableTokensToExpected(
      fragmentMergedTokens,
      expectedTokens,
      targetLanguage,
      expectedProperNameTokens,
    );
  }

  if (expectedTokens.length === 1 && collapsedTokens.length === 1) {
    return areComparableSpeechTokensPassEquivalent(collapsedTokens[0], expectedTokens[0], {
      allowSingleTokenSttVariants: true,
      allowProperNameSttVariants: expectedProperNameTokens.has(expectedTokens[0]),
      targetLanguage,
    })
      ? [expectedTokens[0]]
      : collapsedTokens;
  }

  if (expectedTokens.length === 1 && collapsedTokens.length > 1 && collapsedTokens.length <= 3) {
    const expectedToken = expectedTokens[0];
    const maxFragmentLength = Math.max(3, Math.ceil(expectedToken.length * 0.65));
    const canTreatAsFragments = collapsedTokens.every((token) => token.length <= maxFragmentLength);
    if (
      canTreatAsFragments
      && areJoinedSingleTokenFragmentsEquivalent(
        collapsedTokens,
        expectedToken,
        targetLanguage,
        expectedProperNameTokens,
      )
    ) {
      return [expectedToken];
    }
  }

  if (collapsedTokens.length !== expectedTokens.length) {
    return collapsedTokens;
  }

  return collapsedTokens.map((token, index) => (
    areComparableSpeechTokensPassEquivalent(token, expectedTokens[index], {
      allowSingleTokenSttVariants: false,
      allowProperNameSttVariants: expectedProperNameTokens.has(expectedTokens[index]),
      targetLanguage,
    })
      ? expectedTokens[index]
      : token
  ));
}

function tokenizeRecognizedComparableSpeechTextAgainstExpected(
  recognizedText: string,
  expectedText: string,
  targetLanguage?: SupportedLanguage,
): string[] {
  const comparableExpected = canonicalizeCurrencyFormatting(expectedText, expectedText, targetLanguage);
  const comparableRecognized = canonicalizeCurrencyFormatting(recognizedText, expectedText, targetLanguage);
  return collapseKnownLatinTermsAgainstExpected(
    collapseSpokenLatinInitialismsAgainstExpected(tokenize(comparableRecognized), comparableExpected),
    comparableExpected,
  );
}

function normalizeRecognizedComparableSpeechTextAgainstExpected(
  recognizedText: string,
  expectedText: string,
  targetLanguage?: SupportedLanguage,
): string {
  const expectedTokens = tokenize(canonicalizeCurrencyFormatting(expectedText, expectedText, targetLanguage));
  const recognizedTokens = tokenizeRecognizedComparableSpeechTextAgainstExpected(recognizedText, expectedText, targetLanguage);
  const expectedProperNameTokens = buildExpectedProperNameComparableTokenSet(expectedText);
  return alignRecognizedComparableTokensToExpected(
    recognizedTokens,
    expectedTokens,
    targetLanguage,
    expectedProperNameTokens,
  ).join(" ");
}

const ENGLISH_TIME_CONTEXT_TOKENS = new Set(["at", "around", "by"]);
const ENGLISH_CLOCK_QUALIFIER_TOKENS = new Set(["am", "pm", "oclock"]);

function stripRecognizerAddedEnglishClockQualifiers(
  recognizedTokens: string[],
  expectedTokens: string[],
  targetLanguage?: SupportedLanguage,
): string[] {
  if (!isEnglishSpeechTarget(targetLanguage)) {
    return recognizedTokens;
  }
  const expectedHours = new Set<string>();
  const expectedHourMinutes = new Set<string>();
  for (let index = 1; index < expectedTokens.length; index += 1) {
    if (
      /^\d{1,2}$/.test(expectedTokens[index])
      && ENGLISH_TIME_CONTEXT_TOKENS.has(expectedTokens[index - 1])
    ) {
      expectedHours.add(expectedTokens[index]);
      const minute = expectedTokens[index + 1] || "";
      if (/^\d{1,2}$/.test(minute) && Number(minute) <= 59) {
        expectedHourMinutes.add(`${expectedTokens[index]}:${minute}`);
      }
    }
  }
  if (expectedHours.size === 0 || expectedTokens.some((token) => ENGLISH_CLOCK_QUALIFIER_TOKENS.has(token))) {
    return recognizedTokens;
  }
  return recognizedTokens.filter((token, index) => {
    if (!ENGLISH_CLOCK_QUALIFIER_TOKENS.has(token)) {
      return true;
    }
    const recognizedHour = recognizedTokens[index - 1] || "";
    const recognizedContext = recognizedTokens[index - 2] || "";
    if (expectedHours.has(recognizedHour) && ENGLISH_TIME_CONTEXT_TOKENS.has(recognizedContext)) {
      return false;
    }
    const recognizedMinute = recognizedHour;
    const recognizedHourBeforeMinute = recognizedTokens[index - 2] || "";
    const recognizedMinuteContext = recognizedTokens[index - 3] || "";
    return !expectedHourMinutes.has(`${recognizedHourBeforeMinute}:${recognizedMinute}`)
      || !ENGLISH_TIME_CONTEXT_TOKENS.has(recognizedMinuteContext);
  });
}

export function collapseComparableSpeechRepeatsAgainstExpected(
  recognizedText: string,
  expectedText: string,
  targetLanguage?: SupportedLanguage,
): string {
  const expectedTokens = tokenize(canonicalizeCurrencyFormatting(expectedText, expectedText, targetLanguage));
  const recognizedTokens = tokenizeRecognizedComparableSpeechTextAgainstExpected(recognizedText, expectedText, targetLanguage);
  const collapsedTokens = collapseAdjacentRepeatedComparableTokens(recognizedTokens, expectedTokens);
  return collapsedTokens.join(" ");
}

export function isExpectedAwareComparableSpeechExactMatch(
  recognizedText: string,
  expectedText: string,
  targetLanguage?: SupportedLanguage,
): boolean {
  const normalizedExpected = normalizeComparableSpeechText(canonicalizeCurrencyFormatting(expectedText, expectedText, targetLanguage));
  const normalizedRecognized = normalizeRecognizedComparableSpeechTextAgainstExpected(
    recognizedText,
    expectedText,
    targetLanguage,
  );
  return normalizedRecognized.length > 0 && normalizedRecognized === normalizedExpected;
}

function levenshteinDistance(a: string, b: string): number {
  const aLen = a.length;
  const bLen = b.length;

  if (aLen === 0) {
    return bLen;
  }
  if (bLen === 0) {
    return aLen;
  }

  const matrix: number[][] = Array.from({ length: aLen + 1 }, () =>
    Array.from({ length: bLen + 1 }, () => 0),
  );

  for (let i = 0; i <= aLen; i += 1) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= bLen; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= aLen; i += 1) {
    for (let j = 1; j <= bLen; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[aLen][bLen];
}

function buildHint(
  band: SpeechBand,
  speechEvaluation: SpeechEvaluation,
  uiLanguage: SupportedLanguage,
  expectedText: string,
): string {
  const expected = String(expectedText || "").trim();
  const locale = String(uiLanguage || "").trim().toLowerCase();
  const isTurkish = locale === "tr";
  const isArabic = locale === "ar";

  if (band === "PASS") {
    if (expected) {
      return expected;
    }
    const sample = speechEvaluation.passExamples?.[0];
    if (sample) {
      return sample;
    }
    return isTurkish
      ? "Cok iyi. Soyledigin cumle net anlasildi."
      : isArabic
        ? "ممتاز. الجملة كانت واضحة ومفهومة."
      : "Great. Your sentence was clear and understandable.";
  }

  if (band === "LOW_SCORE") {
    if (expected) {
      return isTurkish
        ? `Yaklasti. Sunu daha net soyle: ${expected}`
        : isArabic
          ? `اقتربت. قل هذه الجملة بوضوح أكبر: ${expected}`
        : `Close. Say this more clearly: ${expected}`;
    }
    return isTurkish
      ? "Anlasiliyor ama daha net olmasi icin biraz daha yavas ve vurgulu soyle."
      : isArabic
        ? "المعنى مفهوم، لكن حاول أن تتكلم ببطء ووضوح أكثر."
      : "It is understandable, but try speaking a bit slower and clearer.";
  }

  if (expected) {
    return isTurkish
      ? `Tekrar dene. Sunu soyle: ${expected}`
      : isArabic
        ? `حاول مرة أخرى. قل: ${expected}`
      : `Try again. Say: ${expected}`;
  }
  return isTurkish
    ? "Cumle yeterince net anlasilmadi. Ornek sesi tekrar dinleyip yeniden dene."
    : isArabic
      ? "لم تكن الجملة واضحة بما يكفي. استمع إلى النموذج ثم أعد المحاولة."
    : "The sentence was not clear enough. Listen to the model again and retry.";
}

export function evaluateSpeechAttempt(
  recognizedText: string,
  expectedText: string,
  speechEvaluation: SpeechEvaluation,
  uiLanguage: SupportedLanguage,
  targetLanguage: SupportedLanguage = uiLanguage,
): SpeechAttemptFeedback {
  const normalizedRecognized = normalizeRecognizedComparableSpeechTextAgainstExpected(
    recognizedText,
    expectedText,
    targetLanguage,
  );
  const normalizedExpected = normalizeComparableSpeechText(canonicalizeCurrencyFormatting(expectedText, expectedText, targetLanguage));
  const locale = String(uiLanguage || "").trim().toLowerCase();
  const isTurkish = locale === "tr";
  const isArabic = locale === "ar";

  if (!normalizedRecognized) {
    return {
      recognizedText: "",
      displayRecognizedText: "",
      score: 0,
      band: "FAIL",
      hint:
        isTurkish
          ? "Ses algilanamadi. Mikrofonu yaklastirip tekrar dene."
          : isArabic
            ? "لم يتم التقاط أي كلام. قرّب الميكروفون وحاول مرة أخرى."
          : "No speech detected. Move closer to the microphone and try again.",
      clarityScore: 0,
      clarityBand: "FAIL",
      taskFitStatus: "unknown",
      scoringReason: "clarity",
    };
  }

  if (!normalizedExpected) {
    return {
      recognizedText,
      displayRecognizedText: recognizedText,
      score: 0.5,
      band: "LOW_SCORE",
      hint:
        isTurkish
          ? "Cumle degerlendirildi, devam edebilirsin."
          : isArabic
            ? "تمت معالجة الجملة. يمكنك المتابعة."
          : "Sentence processed. You can continue.",
      clarityScore: 0.5,
      clarityBand: "LOW_SCORE",
      taskFitStatus: "unknown",
      scoringReason: "clarity",
    };
  }

  const distance = levenshteinDistance(normalizedRecognized, normalizedExpected);
  const maxLen = Math.max(normalizedRecognized.length, normalizedExpected.length, 1);
  const charSimilarity = 1 - distance / maxLen;

  const expectedTokens = tokenize(normalizedExpected);
  const recognizedTokens = tokenize(normalizedRecognized);
  const recognizedTokensUsed = new Array(recognizedTokens.length).fill(false);
  let matchedTokenCount = 0;
  for (const expectedToken of expectedTokens) {
    const recognizedMatchIndex = recognizedTokens.findIndex((recognizedToken, index) => (
      !recognizedTokensUsed[index]
      && areComparableSpeechTokensEquivalent(recognizedToken, expectedToken, {
        allowSingleTokenSttVariants: expectedTokens.length === 1 && recognizedTokens.length === 1,
        targetLanguage,
      })
    ));
    if (recognizedMatchIndex === -1) {
      continue;
    }
    recognizedTokensUsed[recognizedMatchIndex] = true;
    matchedTokenCount += 1;
  }
  const tokenCoverage = expectedTokens.length === 0 ? 0 : matchedTokenCount / expectedTokens.length;
  const isExactSentenceMatch = normalizedRecognized === normalizedExpected;
  const displayRecognizedText = isExactSentenceMatch
    ? buildDisplayRecognizedText(recognizedText, expectedText, targetLanguage)
    : recognizedText;
  const rawScore = 0.65 * charSimilarity + 0.35 * tokenCoverage;
  const scoreCeiling = isExactSentenceMatch ? 1 : STRICT_PASS_SCORE_THRESHOLD - 0.001;
  const score = Math.max(0, Math.min(scoreCeiling, Number(rawScore.toFixed(3))));

  let band: SpeechBand = "FAIL";
  if (score >= STRICT_PASS_SCORE_THRESHOLD) {
    band = isExactSentenceMatch ? "PASS" : "LOW_SCORE";
  } else if (score >= STRICT_LOW_SCORE_THRESHOLD) {
    band = "LOW_SCORE";
  }

  return {
    recognizedText,
    displayRecognizedText,
    score,
    band,
    hint: buildHint(band, speechEvaluation, uiLanguage, expectedText),
    evaluationScope: "full",
    clarityScore: score,
    clarityBand: band,
    taskFitStatus: "unknown",
    scoringReason: "clarity",
  };
}
